import { isSuperAdmin, normalizeRole } from '@/services/authService';

/**
 * 获取存储的token
 */
function getStoredToken() {
  return localStorage.getItem('auth_token');
}

/**
 * 获取存储的用户数据
 */
function getStoredUser() {
  try {
    const userData = localStorage.getItem('user');
    return userData ? JSON.parse(userData) : null;
  } catch (error) {
    console.error('Failed to parse stored user data:', error);
    return null;
  }
}

/**
 * 检查用户是否有指定权限
 */
function hasPermission(user: any, permission: string): boolean {
  if (!user) return false;
  if (isSuperAdmin(user)) return true;
  const permissions = user.permissions || [];
  return Array.isArray(permissions) && permissions.includes(permission);
}

/**
 * 检查用户是否有多个权限中的任意一个
 */
function hasAnyPermission(user: any, permissions: string[]): boolean {
  if (!user) return false;
  if (isSuperAdmin(user)) return true;
  const userPermissions = user.permissions || [];
  return Array.isArray(userPermissions) && permissions.some((p) => userPermissions.includes(p));
}

/**
 * 解析当前登录用户：localStorage 优先于 initialState，避免登录后权限仍用旧会话。
 */
function resolveCurrentUser(initialState?: { currentUser?: any }) {
  const token = getStoredToken();
  const storedUser = getStoredUser();

  if (token && storedUser) {
    return {
      ...storedUser,
      role: normalizeRole(storedUser.role),
    };
  }

  const initialUser = initialState?.currentUser;
  if (!initialUser) return null;

  return {
    ...initialUser,
    role: normalizeRole(initialUser.role),
  };
}

/**
 * @see https://umijs.org/docs/max/access#access
 * */
export default function access(initialState: { currentUser?: any } | undefined) {
  const user = resolveCurrentUser(initialState);
  const token = getStoredToken();
  const isLoggedIn = !!(token && user);

  return {
    // 是否已登录
    isLoggedIn,

    // 是否是管理员 (兼容原有的canAdmin检查)
    canAdmin: isLoggedIn && (isSuperAdmin(user) || user?.role === 'admin'),

    // 是否是超级管理员
    canSuperAdmin: isLoggedIn && isSuperAdmin(user),

    // ========== Dashboard 权限 ==========
    canViewDashboard: isLoggedIn && hasPermission(user, 'dashboard:view'),
    canExecuteDashboard: isLoggedIn && hasPermission(user, 'dashboard:execute'),

    // ========== 产品管理权限 ==========
    canViewProducts: isLoggedIn && hasPermission(user, 'product:view'),
    canSyncProducts: isLoggedIn && hasPermission(user, 'product:sync'),
    canManageProducts:
      isLoggedIn && hasAnyPermission(user, ['product:sync', 'product:delete', 'product:unpublish']),
    canManageMetafields:
      isLoggedIn && (isSuperAdmin(user) || hasPermission(user, 'product:metafield_manage')),

    // ========== 订单管理权限 ==========
    canViewOrders: isLoggedIn && hasPermission(user, 'order:view'),
    canSyncOrders: isLoggedIn && hasPermission(user, 'order:sync'),

    // ========== 日志管理权限 ==========
    canViewLogs: isLoggedIn && hasPermission(user, 'log:view'),
    canExportLogs: isLoggedIn && hasPermission(user, 'log:export'),

    // ========== 系统管理权限 ==========
    canManageUsers:
      isLoggedIn &&
      (isSuperAdmin(user) || hasAnyPermission(user, ['user:create', 'user:update', 'user:delete'])),
    canManageRoles:
      isLoggedIn &&
      (isSuperAdmin(user) || hasAnyPermission(user, ['role:create', 'role:update', 'role:delete'])),
    canManagePermissions:
      isLoggedIn &&
      (isSuperAdmin(user) ||
        hasAnyPermission(user, ['permission:create', 'permission:update', 'permission:delete'])),
    canManageSettings: isLoggedIn && (isSuperAdmin(user) || hasPermission(user, 'admin:setting')),

    // ========== DEAR 数据权限 ==========
    canManageDear:
      isLoggedIn &&
      hasAnyPermission(user, ['dear:manual_sync', 'dear:sync_by_barcode', 'dear:backfeed']),
  };
}
